import { format } from 'date-fns'
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@/components/ui/table'

export interface StatusCallResult {
  id: string
  phone_number: string
  intone_call_status?: string
  amd_status?: string
  started_at: string | null
  client_answered_at: string | null
  bot_answered_at: string | null
  manager_answered_at: string | null
  ended_at: string | null
  country_iso?: string
  country_state?: string
  timezone?: string
  list: {
    name: string
  }
}

function formatTime(date: Date | string | null): string {
  if (!date) return '-'
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date
    if (isNaN(dateObj.getTime())) return '-'
    return format(dateObj, 'HH:mm:ss')
  } catch {
    return '-'
  }
}

interface CallsTableProps {
  calls: StatusCallResult[]
  tab: string
  onPhoneClick?: (phoneNumber: string) => void
}

export function CallsTable({ calls, tab, onPhoneClick }: CallsTableProps) {
  const handlePhoneClick = (phoneNumber: string) => {
    if (onPhoneClick) {
      onPhoneClick(phoneNumber)
    } else {
      window.open(`/dialer/call-logs?phone=${encodeURIComponent(phoneNumber)}`, '_blank')
    }
  }

  if (tab === 'queue') {
    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Phone Number</TableHead>
            <TableHead>List</TableHead>
            <TableHead>Country</TableHead>
            <TableHead>State</TableHead>
            <TableHead>Timezone</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {calls.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                No calls in queue
              </TableCell>
            </TableRow>
          ) : (
            calls.map((call) => (
              <TableRow key={call.id}>
                <TableCell>
                  <button
                    onClick={() => handlePhoneClick(call.phone_number)}
                    className="text-blue-600 hover:underline"
                  >
                    {call.phone_number}
                  </button>
                </TableCell>
                <TableCell>{call.list.name || 'Unknown'}</TableCell>
                <TableCell>{call.country_iso || '-'}</TableCell>
                <TableCell>{call.country_state || '-'}</TableCell>
                <TableCell>{call.timezone || '-'}</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    )
  }

  if (tab === 'transfers') {
    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Phone Number</TableHead>
            <TableHead>List</TableHead>
            <TableHead>Call Status</TableHead>
            <TableHead>Started</TableHead>
            <TableHead>Answered</TableHead>
            <TableHead>Bot Answered</TableHead>
            <TableHead>Escalated</TableHead>
            <TableHead>Mgr Answered</TableHead>
            <TableHead>Ended</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {calls.length === 0 ? (
            <TableRow>
              <TableCell colSpan={9} className="text-center text-muted-foreground py-8">
                No transferred calls found
              </TableCell>
            </TableRow>
          ) : (
            calls.map((call) => (
              <TableRow key={call.id}>
                <TableCell>
                  <button
                    onClick={() => handlePhoneClick(call.phone_number)}
                    className="text-blue-600 hover:underline"
                  >
                    {call.phone_number}
                  </button>
                </TableCell>
                <TableCell>{call.list.name || 'Unknown'}</TableCell>
                <TableCell>{call.intone_call_status || '-'}</TableCell>
                <TableCell>{formatTime(call.started_at)}</TableCell>
                <TableCell>{formatTime(call.client_answered_at)}</TableCell>
                <TableCell>{formatTime(call.bot_answered_at)}</TableCell>
                <TableCell>{formatTime(call.manager_answered_at)}</TableCell>
                <TableCell>{formatTime(call.manager_answered_at)}</TableCell>
                <TableCell>{formatTime(call.ended_at)}</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    )
  }

  // Default table (last, na, progress)
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Phone Number</TableHead>
          <TableHead>List</TableHead>
          <TableHead>Call Status</TableHead>
          <TableHead>AMD</TableHead>
          <TableHead>Started</TableHead>
          <TableHead>Answered</TableHead>
          <TableHead>Bot Answered</TableHead>
          <TableHead>Escalated</TableHead>
          <TableHead>Ended</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {calls.length === 0 ? (
          <TableRow>
            <TableCell colSpan={9} className="text-center text-muted-foreground py-8">
              No calls found
            </TableCell>
          </TableRow>
        ) : (
          calls.map((call) => (
            <TableRow key={call.id}>
              <TableCell>
                <button
                  onClick={() => handlePhoneClick(call.phone_number)}
                  className="text-blue-600 hover:underline"
                >
                  {call.phone_number}
                </button>
              </TableCell>
              <TableCell>{call.list.name || 'Unknown'}</TableCell>
              <TableCell>{call.intone_call_status || '-'}</TableCell>
              <TableCell>{call.amd_status || '-'}</TableCell>
              <TableCell>{formatTime(call.started_at)}</TableCell>
              <TableCell>{formatTime(call.client_answered_at)}</TableCell>
              <TableCell>{formatTime(call.bot_answered_at)}</TableCell>
              <TableCell>{formatTime(call.manager_answered_at)}</TableCell>
              <TableCell>{formatTime(call.ended_at)}</TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  )
}
